import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import ScoreBar from "@/components/ScoreBar";
import WaitlistDialog from "@/components/WaitlistDialog";
import AnalysisProgress from "@/components/AnalysisProgress";
import CopyBlock from "@/components/CopyBlock";
import {
  DownloadSimple, FileText, Check, X, WarningCircle, ArrowRight, Sparkle,
} from "@phosphor-icons/react";

function Label({ children, htmlFor }) {
  return <label htmlFor={htmlFor} className="label-mono mb-1.5 block">{children}</label>;
}

export default function CatalogAnalyzer() {
  const [params] = useSearchParams();
  const [input, setInput] = useState(params.get("q") || "");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [blocked, setBlocked] = useState(null);
  const [manualMode, setManualMode] = useState("paste"); // "paste" | "fields"
  const [pasteText, setPasteText] = useState("");
  const [manual, setManual] = useState({ title: "", bullets: "", price: "", description: "" });
  const [usage, setUsage] = useState(null);
  const [error, setError] = useState("");
  const [rateLimited, setRateLimited] = useState(false);

  const refreshUsage = () => api.get("/tools/usage").then((r) => setUsage(r.data));
  useEffect(() => { refreshUsage(); }, []);
  useEffect(() => {
    if (input && params.get("q")) submit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async () => {
    if (!input.trim()) { toast.error("Paste an Amazon URL or ASIN"); return; }
    setLoading(true); setError(""); setResult(null); setBlocked(null); setRateLimited(false);
    try {
      const isAsin = /^[A-Z0-9]{10}$/.test(input.trim());
      const body = isAsin
        ? { input_type: "asin", asin: input.trim() }
        : { input_type: "url", url: input.trim() };
      const { data } = await api.post("/tools/catalog-analyzer", body);
      if (data.blocked) setBlocked(data); else setResult(data);
      await refreshUsage();
    } catch (e) {
      if (e?.response?.status === 429) { setRateLimited(true); setError(apiError(e)); }
      else setError(apiError(e));
    } finally { setLoading(false); }
  };

  const submitManual = async () => {
    setLoading(true); setError(""); setBlocked(null);
    try {
      let body;
      if (manualMode === "paste") {
        if (!pasteText.trim() || pasteText.trim().length < 50) {
          toast.error("Paste the full Amazon page contents (at least a paragraph)");
          setLoading(false);
          return;
        }
        body = { input_type: "paste", raw_text: pasteText, asin: blocked?.asin };
      } else {
        const bullets = manual.bullets.split("\n").map((s) => s.trim()).filter(Boolean);
        body = {
          input_type: "manual",
          title: manual.title, bullets, price: manual.price, description: manual.description,
          asin: blocked?.asin,
        };
      }
      const { data } = await api.post("/tools/catalog-analyzer", body);
      setResult(data);
      await refreshUsage();
    } catch (e) {
      if (e?.response?.status === 429) { setRateLimited(true); setError(apiError(e)); }
      else setError(apiError(e));
    } finally { setLoading(false); }
  };

  const u = usage?.tools?.["catalog-analyzer"];

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
      <ToolHeader no="01" title="Catalog Analyzer"
        subtitle="Paste an Amazon URL or 10-character ASIN. We pull the listing and the analyst scores it."
        used={u?.used ?? 0} remaining={u?.remaining ?? 5} limit={usage?.limit ?? 5} isAdmin={usage?.is_admin} />

      {/* Brutalist input + button (joined block) */}
      <div className="mt-10 flex flex-col sm:flex-row border border-ink">
        <Input data-testid="ca-input"
          placeholder="https://www.amazon.com/dp/B0XXXXXXXX  or  B0XXXXXXXX"
          value={input} onChange={(e) => setInput(e.target.value)}
          className="h-14 border-0 rounded-none flex-1 focus-visible:ring-0 focus-visible:border-0 bg-white" />
        <Button onClick={submit} disabled={loading} data-testid="ca-submit"
          className="h-14 px-6 text-base border-0 rounded-none sm:border-l sm:border-ink">
          {loading ? "Analyzing…" : <>Run analysis <ArrowRight className="w-4 h-4 ml-2" weight="bold" /></>}
        </Button>
      </div>

      <AnalysisProgress
        active={loading}
        done={!!result?.analysis}
        label="Catalog analysis"
        stages={[
          { title: "Fetching listing from Amazon", ms: 2500, detail: "provider chain" },
          { title: "Parsing title, bullets, images, price", ms: 1500 },
          { title: "Scoring SEO, content, pricing, images", ms: 2500 },
          { title: "Generating prioritized recommendations", ms: 2500 },
          { title: "Writing copy-paste-ready optimized listing", ms: 3500 },
        ]}
      />

      {rateLimited && <RateLimited />}
      {error && !rateLimited && (
        <div className="mt-6 border border-alert/30 bg-alert-bg text-alert px-4 py-3 text-sm flex items-start gap-3" data-testid="ca-error">
          <WarningCircle className="w-5 h-5 shrink-0 mt-0.5" />
          <div>{error}</div>
        </div>
      )}

      {blocked && (
        <div className="mt-8 border border-gold/40 bg-gold-bg p-6 md:p-8" data-testid="ca-manual">
          <div className="flex items-start gap-4">
            <WarningCircle weight="duotone" className="w-6 h-6 text-gold shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-display text-xl font-bold tracking-tight text-ink">{blocked.message}</div>
              <p className="text-sm text-ink/70 mt-2">
                Amazon blocks server scrapers from datacenter IPs. Two ways to continue —
                <strong className="font-medium"> the paste option is much faster:</strong>
              </p>

              {/* Mode tabs */}
              <div className="mt-5 inline-flex border border-ink bg-white">
                <button
                  type="button"
                  onClick={() => setManualMode("paste")}
                  data-testid="ca-mode-paste"
                  className={`px-4 py-2 label-mono text-[11px] transition-colors ${manualMode === "paste" ? "bg-ink text-white" : "text-ink/60 hover:text-ink"}`}
                >
                  Paste full page · fast
                </button>
                <button
                  type="button"
                  onClick={() => setManualMode("fields")}
                  data-testid="ca-mode-fields"
                  className={`px-4 py-2 label-mono text-[11px] transition-colors border-l border-ink ${manualMode === "fields" ? "bg-ink text-white" : "text-ink/60 hover:text-ink"}`}
                >
                  Fill fields manually
                </button>
              </div>

              {manualMode === "paste" ? (
                <div className="mt-5">
                  <Label>Paste anything from the Amazon listing</Label>
                  <p className="text-xs text-ink/55 mt-1 mb-2 leading-relaxed">
                    On the Amazon page, hit <span className="font-mono bg-white border border-rule px-1.5 py-0.5">Cmd/Ctrl+A</span> then <span className="font-mono bg-white border border-rule px-1.5 py-0.5">Cmd/Ctrl+C</span> and paste here. The analyst will extract title, price, bullets, and description automatically.
                  </p>
                  <Textarea
                    rows={10}
                    value={pasteText}
                    onChange={(e) => setPasteText(e.target.value)}
                    placeholder={"Stainless Steel Water Bottle — 32oz Vacuum Insulated…\n$24.99\n4.6 out of 5 stars · 1,247 ratings\nAbout this item\n• Keeps drinks cold 24h / hot 12h…"}
                    data-testid="ca-paste-text"
                  />
                </div>
              ) : (
                <div className="grid md:grid-cols-2 gap-5 mt-5">
                  <div>
                    <Label>Title</Label>
                    <Input value={manual.title} onChange={(e) => setManual({ ...manual, title: e.target.value })}
                      data-testid="ca-manual-title" />
                  </div>
                  <div>
                    <Label>Price</Label>
                    <Input value={manual.price} onChange={(e) => setManual({ ...manual, price: e.target.value })}
                      data-testid="ca-manual-price" />
                  </div>
                  <div className="md:col-span-2">
                    <Label>Bullet points · one per line</Label>
                    <Textarea rows={5} value={manual.bullets}
                      onChange={(e) => setManual({ ...manual, bullets: e.target.value })}
                      data-testid="ca-manual-bullets" />
                  </div>
                  <div className="md:col-span-2">
                    <Label>Description · optional</Label>
                    <Textarea rows={3} value={manual.description}
                      onChange={(e) => setManual({ ...manual, description: e.target.value })}
                      data-testid="ca-manual-desc" />
                  </div>
                </div>
              )}

              <Button className="mt-5" onClick={submitManual} disabled={loading} data-testid="ca-manual-submit">
                {loading ? "Analyzing…" : "Analyze these details"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {result?.analysis && <ResultView product={result.product} analysis={result.analysis} />}
    </div>
  );
}

function ResultView({ product, analysis }) {
  const sub = analysis?.scores || {};
  const recs = analysis?.recommendations || [];
  const kws = analysis?.keywords || [];
  const comps = analysis?.competitors || [];
  const overall = analysis.overallScore || 0;

  const downloadCSV = () => {
    const rows = [["Keyword", "Relevance", "Competition"], ...kws.map((k) => [k.keyword, k.relevance, k.competition])];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "keywords.csv";
    a.click();
  };

  const downloadPDF = () => {
    const esc = (s) =>
      String(s ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
    const w = window.open("", "_blank");
    w.document.write(`
      <html><head><title>CliqCommerce Report</title>
      <style>
        body{font-family:'IBM Plex Mono',ui-monospace,monospace;color:#111;padding:40px;max-width:780px;margin:auto;line-height:1.5}
        h1{font-family:'Cabinet Grotesk',sans-serif;font-size:32px;margin:0 0 4px;letter-spacing:-0.03em;font-weight:900}
        h2{font-family:'Cabinet Grotesk',sans-serif;font-size:22px;letter-spacing:-0.02em;margin:32px 0 8px}
        h3{font-family:'Cabinet Grotesk',sans-serif;font-size:16px;text-transform:uppercase;letter-spacing:0.12em;color:#666;margin:28px 0 8px}
        table{width:100%;border-collapse:collapse;margin-top:10px;font-size:12px}
        td,th{border-bottom:1px solid #e5e5e5;padding:8px 10px;text-align:left}
        th{text-transform:uppercase;letter-spacing:0.12em;color:#666;font-size:10px;border-bottom:1px solid #111;font-weight:500}
        .pri-high{color:#b91c1c;font-weight:700}.pri-medium{color:#b45309}.pri-low{color:#15803d}
        .meta{color:#666;font-size:11px}
      </style></head><body>
      <div class="meta">CLIQCOMMERCE · CATALOG REPORT</div>
      <h1>${esc(product?.title || "Untitled listing")}</h1>
      <h2>Overall · ${esc(overall)}/100</h2>
      <p>SEO ${esc(sub.seo)} · Images ${esc(sub.images)} · Pricing ${esc(sub.pricing)} · Content ${esc(sub.content)}</p>
      <h3>Recommendations</h3>
      <table><tr><th>Priority</th><th>Tag</th><th>Title</th><th>Description</th></tr>
      ${recs.map((r) => `<tr><td class="pri-${esc(r.priority)}">${esc(String(r.priority || "").toUpperCase())}</td><td>${esc(r.category)}</td><td>${esc(r.title)}</td><td>${esc(r.description)}</td></tr>`).join("")}
      </table>
      <h3>Keywords</h3>
      <table><tr><th>Keyword</th><th>Relevance</th><th>Competition</th></tr>
      ${kws.map((k) => `<tr><td>${esc(k.keyword)}</td><td>${esc(k.relevance)}</td><td>${esc(k.competition)}</td></tr>`).join("")}
      </table>
      <h3>Competitor comparison</h3>
      <table><tr><th>Aspect</th><th>Your product</th><th>Competitor</th><th>Adv.</th></tr>
      ${comps.map((c) => `<tr><td>${esc(c.aspect)}</td><td>${esc(c.yourProduct)}</td><td>${esc(c.competitor)}</td><td>${c.advantage ? "✓" : "✗"}</td></tr>`).join("")}
      </table>
      </body></html>`);
    w.document.close();
    setTimeout(() => w.print(), 400);
  };

  const overallColor = overall >= 75 ? "text-signal" : overall >= 50 ? "text-gold" : "text-alert";

  return (
    <div className="mt-12 space-y-8" data-testid="ca-result">
      {/* Score block */}
      <div className="border border-rule bg-white">
        <div className="flex items-center justify-between border-b border-rule px-6 py-4 label-mono">
          <span>Verdict · {product?.asin || "—"}</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={downloadCSV} data-testid="ca-csv-btn"><DownloadSimple className="w-3.5 h-3.5 mr-1.5" /> CSV</Button>
            <Button variant="outline" size="sm" onClick={downloadPDF} data-testid="ca-pdf-btn"><FileText className="w-3.5 h-3.5 mr-1.5" /> PDF</Button>
          </div>
        </div>
        <div className="grid md:grid-cols-12 gap-10 p-6 md:p-8">
          <div className="md:col-span-5">
            <div className="text-sm text-ink/65 line-clamp-2 mb-6 leading-relaxed">{product?.title}</div>
            <div className="label-mono">Overall score</div>
            <div className={`mt-2 font-display font-black text-8xl tracking-tightest tabular ${overallColor} leading-none`}>
              {overall}<span className="text-ink/20 text-4xl">/100</span>
            </div>
            <div className="mt-6"><ScoreBar value={overall} showValue={false} /></div>
          </div>
          <div className="md:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-5">
            {["seo", "images", "pricing", "content"].map((k) => (
              <ScoreBar key={k} label={k} value={sub[k] ?? 0} />
            ))}
          </div>
        </div>
      </div>

      <Section no="02" title="Recommendations">
        <div className="border-t border-rule">
          {recs.map((r, i) => (
            <div key={`${r.title}-${i}`} className="grid md:grid-cols-12 gap-6 px-6 py-5 border-b border-rule">
              <div className="md:col-span-2 flex items-start gap-3">
                <span className={`label-mono px-1.5 py-0.5 border ${prioStyle(r.priority)}`}>{(r.priority || "").toUpperCase()}</span>
                <span className="label-mono text-ink/50">{(r.category || "").toUpperCase()}</span>
              </div>
              <div className="md:col-span-10">
                <div className="font-medium text-ink">{r.title}</div>
                <p className="mt-1 text-sm text-ink/65 leading-relaxed">{r.description}</p>
              </div>
            </div>
          ))}
        </div>
      </Section>

      <RecommendedCopySection copy={analysis.recommended_copy} />

      <Section no="04" title="SEO keywords">
        <Table>
          <TableHeader>
            <TableRow className="border-b border-ink">
              <TableHead className="label-mono">Keyword</TableHead>
              <TableHead className="label-mono w-32">Relevance</TableHead>
              <TableHead className="label-mono w-32">Competition</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {kws.map((k, i) => (
              <TableRow key={`${k.keyword}-${i}`} className="hover:bg-surface-hover">
                <TableCell className="font-medium">{k.keyword}</TableCell>
                <TableCell className="font-mono tabular">{k.relevance}</TableCell>
                <TableCell><Badge variant="outline" className={compStyle(k.competition)}>{k.competition}</Badge></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Section>

      <Section no="05" title="Competitor comparison">
        <Table>
          <TableHeader>
            <TableRow className="border-b border-ink">
              <TableHead className="label-mono">Aspect</TableHead>
              <TableHead className="label-mono">Your product</TableHead>
              <TableHead className="label-mono">Competitor</TableHead>
              <TableHead className="label-mono w-20">Adv.</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {comps.map((c, i) => (
              <TableRow key={`${c.aspect}-${i}`} className="hover:bg-surface-hover">
                <TableCell className="font-medium">{c.aspect}</TableCell>
                <TableCell>{c.yourProduct}</TableCell>
                <TableCell className="text-ink/65">{c.competitor}</TableCell>
                <TableCell>{c.advantage ? <Check className="w-5 h-5 text-signal" weight="bold" /> : <X className="w-5 h-5 text-alert" weight="bold" />}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Section>
    </div>
  );
}

function Section({ no, title, children, accent }) {
  return (
    <div className={`border bg-white ${accent ? "border-ink" : "border-rule"}`}>
      <div className={`border-b border-rule px-6 py-4 flex items-center justify-between label-mono ${accent ? "bg-butter text-ink" : ""}`}>
        <span>{title}</span>
        <span className="text-ink/40">№ {no}</span>
      </div>
      {children}
    </div>
  );
}

function RecommendedCopySection({ copy }) {
  if (!copy) return null;
  const bullets = Array.isArray(copy.bullets) ? copy.bullets : [];
  return (
    <Section no="03" title="Copy-paste-ready listing · drop straight into Seller Central" accent>
      <div className="p-6 space-y-5">
        <p className="text-sm text-ink/70 leading-relaxed">
          Optimized for Amazon's A9 algorithm <strong>and</strong> AI search engines (Rufus, Perplexity, ChatGPT Shopping). Just copy each block and paste into the matching Seller Central field.
        </p>
        {copy.title && (
          <CopyBlock title="Product title" value={copy.title}
            hint="Paste into Product Identity → Item name"
            charLimit={200} testid="copy-title" />
        )}
        {bullets.length > 0 && (
          <CopyBlock title="Key product features · 5 bullets" value={bullets}
            hint="Paste into Product Identity → Key product features"
            testid="copy-bullets" />
        )}
        {copy.description && (
          <CopyBlock title="Product description" value={copy.description}
            hint="Paste into Product Identity → Product description"
            testid="copy-description" />
        )}
        {copy.search_terms && (
          <CopyBlock title="Backend search terms" value={copy.search_terms}
            hint="Paste into Keywords → Search terms (Amazon limit: 250 bytes)"
            charLimit={250} testid="copy-search-terms" />
        )}
        {(copy.a_plus_headline || copy.a_plus_subhead) && (
          <div className="grid md:grid-cols-2 gap-4">
            {copy.a_plus_headline && (
              <CopyBlock title="A+ headline" value={copy.a_plus_headline}
                hint="Hero module · headline" charLimit={60} testid="copy-aplus-headline" />
            )}
            {copy.a_plus_subhead && (
              <CopyBlock title="A+ sub-headline" value={copy.a_plus_subhead}
                hint="Hero module · sub-head" charLimit={140} testid="copy-aplus-subhead" />
            )}
          </div>
        )}
      </div>
    </Section>
  );
}

function prioStyle(p) {
  return p === "high" ? "border-alert text-alert"
    : p === "medium" ? "border-gold text-gold"
    : "border-signal text-signal";
}
function compStyle(c) {
  return c === "high" ? "border-alert/40 text-alert"
    : c === "medium" ? "border-gold/40 text-gold"
    : "border-signal/40 text-signal";
}

export function ToolHeader({ no, title, subtitle, used, remaining, limit, isAdmin }) {
  return (
    <div className="grid md:grid-cols-12 gap-8 items-end">
      <div className="md:col-span-8">
        <div className="label-mono">Analyst · № {no}</div>
        <h1 className="mt-3 font-display font-black text-5xl md:text-6xl tracking-tightest text-ink leading-[1]">{title}</h1>
        <p className="mt-4 text-ink/65 max-w-2xl leading-relaxed">{subtitle}</p>
      </div>
      <div className="md:col-span-4 md:text-right" data-testid="tool-quota">
        <div className="label-mono">Today</div>
        <div className="mt-2 font-mono tabular text-3xl text-ink">
          {isAdmin ? "∞" : `${remaining}/${limit}`}
        </div>
        <div className="label-mono mt-1 text-ink/50">
          {isAdmin ? "admin · unlimited" : `${used} used`}
        </div>
      </div>
    </div>
  );
}

export function RateLimited() {
  return (
    <div className="mt-8 border border-ink bg-ink text-white p-6 md:p-8 flex flex-col md:flex-row items-start gap-6" data-testid="rate-limited">
      <Sparkle weight="duotone" className="w-8 h-8 shrink-0 mt-1" />
      <div className="flex-1">
        <div className="label-mono text-white/60">Daily limit reached</div>
        <div className="mt-2 font-display text-2xl font-bold tracking-tight">
          You've used today's 5 free runs on this analyst.
        </div>
        <p className="text-white/70 text-sm mt-2 leading-relaxed">
          Resets at midnight (Asia/Kolkata). Join the waitlist for higher limits at launch plus priority access.
        </p>
        <div className="mt-5">
          <WaitlistDialog trigger={<Button className="bg-white text-ink hover:bg-white/90 border-white" data-testid="ratelimit-waitlist">Join the waitlist →</Button>} />
        </div>
      </div>
    </div>
  );
}
